import React, { useEffect, useState } from "react";
import { storage, firestore } from "../../firebase-config";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";

const AdminUploadPage = () => {
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState("");
  const [fileType, setFileType] = useState("");
  const [publications, setPublications] = useState([]);
  const [editingPublication, setEditingPublication] = useState(null);
  const [newFile, setNewFile] = useState(null);
  const [newName, setNewName] = useState("");

  // Fetch all publications on mount
  const fetchPublications = async () => {
    try {
      const querySnapshot = await getDocs(collection(firestore, "publications"));
      const pubs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPublications(pubs);
    } catch (error) {
      setMessage("Failed to fetch publications.");
      console.error(error);
    }
  };

  useEffect(() => {
    fetchPublications();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setFileType(selectedFile?.type || "");
  };

  const handleNewFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setNewFile(selectedFile);
  };

  const resetForm = () => {
    setFile(null);
    setFileType("");
    setProgress(0);
    setMessage("");
  };

  const resetEditForm = () => {
    setEditingPublication(null);
    setNewFile(null);
    setNewName("");
    setProgress(0);
    setMessage("");
  };

  // Upload new publication
  const handleUpload = () => {
    if (!file) {
      setMessage("Please select a file to upload");
      return;
    }
    setMessage("");
    const storageRef = ref(storage, `publications/${Date.now()}_${file.name}`);
    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progressPercent =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setProgress(Math.round(progressPercent));
      },
      (error) => {
        setMessage("Upload failed. Please try again later.");
        console.error(error);
      },
      async () => {
        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
        try {
          const docRef = await addDoc(collection(firestore, "publications"), {
            name: file.name,
            url: downloadURL,
            type: fileType,
            storagePath: uploadTask.snapshot.ref.fullPath,
            uploadedAt: new Date(),
          });
          setPublications([
            ...publications,
            {
              id: docRef.id,
              name: file.name,
              url: downloadURL,
              type: fileType,
              storagePath: uploadTask.snapshot.ref.fullPath,
              uploadedAt: new Date(),
            },
          ]);
          setMessage("File uploaded successfully!");
          resetForm();
        } catch (error) {
          setMessage("Error saving file metadata in Firestore");
          console.error(error);
        }
      }
    );
  };

  // Delete publication
  const handleDelete = async (id, storagePath) => {
    if (!window.confirm("Are you sure you want to delete this publication?")) return;
    try {
      // Delete from Storage
      const imageRef = ref(storage, storagePath);
      await deleteObject(imageRef);

      // Delete Firestore doc
      await deleteDoc(doc(firestore, "publications", id));

      // Update state
      setPublications(publications.filter((pub) => pub.id !== id));
      setMessage("Publication deleted successfully.");
    } catch (error) {
      setMessage("Failed to delete publication.");
      console.error(error);
    }
  };

  // Start editing
  const handleEdit = (pub) => {
    setEditingPublication(pub);
    setNewName(pub.name);
    setNewFile(null);
    setMessage("");
  };

  // Update publication metadata and optionally file
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingPublication) return;

    setMessage("");
    try {
      let updatedUrl = editingPublication.url;
      let updatedStoragePath = editingPublication.storagePath;

      if (newFile) {
        // Upload new file to storage
        const storageRef = ref(storage, `publications/${Date.now()}_${newFile.name}`);
        const uploadTask = uploadBytesResumable(storageRef, newFile);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progressPercent =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setProgress(Math.round(progressPercent));
            },
            (error) => reject(error),
            () => resolve()
          );
        });

        updatedUrl = await getDownloadURL(uploadTask.snapshot.ref);
        updatedStoragePath = uploadTask.snapshot.ref.fullPath;

        // Delete old file from storage
        const oldFileRef = ref(storage, editingPublication.storagePath);
        await deleteObject(oldFileRef);
      }

      // Update Firestore doc
      await updateDoc(doc(firestore, "publications", editingPublication.id), {
        name: newName,
        url: updatedUrl,
        storagePath: updatedStoragePath,
        updatedAt: new Date(),
      });

      setPublications(
        publications.map((pub) =>
          pub.id === editingPublication.id
            ? { ...pub, name: newName, url: updatedUrl, storagePath: updatedStoragePath }
            : pub
        )
      );

      setMessage("Publication updated successfully!");
      resetEditForm();
    } catch (error) {
      setMessage("Failed to update publication.");
      console.error(error);
    }
  };

  return (
    <div className="upload-page" style={{ maxWidth: 900, margin: "auto", padding: 20, fontFamily: "Arial, sans-serif" }}>
      <h2>{editingPublication ? "Edit Publication" : "Admin File Upload"}</h2>

      {editingPublication ? (
        <form onSubmit={handleUpdate} style={{ marginBottom: 30 }}>
          <div style={{ marginBottom: 10 }}>
            <label>
              Publication Name:
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                required
                style={{ marginLeft: 10, padding: 6, width: "70%" }}
              />
            </label>
          </div>

          <div style={{ marginBottom: 10 }}>
            <label>
              Upload New File (optional):
              <input
                type="file"
                onChange={handleNewFileChange}
                style={{ marginLeft: 10 }}
              />
            </label>
          </div>

          <button
            type="submit"
            style={{
              padding: "10px 20px",
              marginRight: 10,
              backgroundColor: "#004d80",
              color: "white",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Update
          </button>
          <button
            type="button"
            onClick={resetEditForm}
            style={{
              padding: "10px 20px",
              backgroundColor: "#888",
              color: "white",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Cancel
          </button>
          {progress > 0 && <p>Upload Progress: {progress}%</p>}
        </form>
      ) : (
        <>
          <input type="file" onChange={handleFileChange} />
          {file && <p>Selected File: {file.name}</p>}
          {file && <p>File Type: {fileType}</p>}
          <button
            onClick={handleUpload}
            style={{
              marginTop: 10,
              padding: "10px 20px",
              backgroundColor: "#004d80",
              color: "white",
              border: "none",
              borderRadius: 5,
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            Upload File
          </button>
          {progress > 0 && <p>Upload Progress: {progress}%</p>}
        </>
      )}

      {message && <p style={{ marginTop: 15, color: message.includes("failed") || message.includes("Error") ? "red" : "green" }}>{message}</p>}

      <h3 style={{ marginTop: 40, marginBottom: 20 }}>Uploaded Publications</h3>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 20,
          justifyContent: "flex-start",
        }}
      >
        {publications.length === 0 ? (
          <p>No publications uploaded yet.</p>
        ) : (
          publications.map((pub) => (
            <div
              key={pub.id}
              style={{
                border: "1px solid #ccc",
                borderRadius: 8,
                padding: 15,
                width: 240,
                backgroundColor: "#f9f9f9",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                height: 180,
              }}
            >
              <div>
                <p style={{ fontWeight: "bold", marginBottom: 8, wordBreak: "break-word" }}>{pub.name}</p>
                <p style={{ fontSize: 14, marginBottom: 12, color: "#555" }}>
                  Type: {pub.type || "N/A"}
                </p>
                <a
                  href={pub.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ color: "#004d80", textDecoration: "underline" }}
                >
                  View File
                </a>
              </div>
              <div style={{ marginTop: 15, display: "flex", justifyContent: "space-between" }}>
                <button
                  onClick={() => handleEdit(pub)}
                  style={{
                    backgroundColor: "#007bff",
                    color: "white",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: 5,
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: 14,
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(pub.id, pub.storagePath)}
                  style={{
                    backgroundColor: "#dc3545",
                    color: "white",
                    border: "none",
                    padding: "6px 12px",
                    borderRadius: 5,
                    cursor: "pointer",
                    fontWeight: "bold",
                    fontSize: 14,
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminUploadPage;

